<template>
  <Table v-model:sort="sortModel" class="flex min-h-0 flex-1 flex-col">
    <Table.Head>
      <Table.Header primary>Your registration</Table.Header>
      <Table.Header class="w-[50px]" />
      <Table.Header class="flex-1">Flagged work</Table.Header>
      <Table.Header class="w-[180px]" field="timestamp" sortable>
        Date
      </Table.Header>
      <Table.Header class="w-[180px]">Infringement match</Table.Header>
      <Table.Header class="w-[120px]">Status</Table.Header>
      <Table.Header class="w-[100px]" />
    </Table.Head>

    <div class="relative min-h-0 flex-1">
      <div
        v-if="processing"
        class="border--gradient pointer-events-none absolute inset-x-0 -top-px z-30 h-px"
      />

      <div
        ref="scrollElement"
        class="h-full min-h-0 overflow-auto"
        @scroll="emit('scroll', $event)"
      >
        <NoInfringements v-if="empty" />

        <template v-else-if="loading">
          <div
            v-for="n in 6"
            :key="n"
            class="flex min-h-[52px] items-stretch border-b border-[#EFEFEF]"
          >
            <Table.Cell primary class="h-[52px]! pl-[7px]">
              <Skeleton class="mr-2.5 size-[35px] shrink-0" />
              <Skeleton class="h-[14px] w-[120px]" />
            </Table.Cell>
            <div class="w-[50px] shrink-0" />
            <Table.Cell class="h-[52px]! flex-1 pl-[7px]">
              <Skeleton class="mr-2.5 size-[35px] shrink-0" />
              <Skeleton class="h-[14px] w-[100px]" />
            </Table.Cell>
            <Table.Cell class="h-[52px]! w-[180px]">
              <Skeleton class="h-[14px] w-[100px]" />
            </Table.Cell>
            <Table.Cell class="h-[52px]! w-[180px]">
              <Skeleton class="h-2 w-[120px]" />
            </Table.Cell>
            <Table.Cell class="h-[52px]! w-[120px]">
              <Skeleton class="h-[22px] w-[70px]" />
            </Table.Cell>
            <Table.Cell class="h-[52px]! w-[100px]" />
          </div>
        </template>

        <Table.Row
          v-for="infringement in infringements"
          v-else
          :key="infringement.id"
          :class="isProcessing(infringement) && 'cursor-default!'"
          @row-click="onRowClick(infringement)"
        >
          <Table.Cell primary class="h-[52px]! pl-[7px]">
            <template v-if="isGroup(infringement)">
              <div
                class="relative mr-2.5 flex size-[35px] shrink-0 items-center justify-center"
              >
                <div
                  v-for="(c, i) in infringement.copyrights.slice(0, 3)"
                  :key="c.id"
                  class="absolute inset-0"
                  :style="stackedStyle(i, infringement.copyrights.length)"
                >
                  <Thumbnail
                    :type="fileTypeToThumbnail(c.type)"
                    :src="previewImageUrl(c.asset?.previewImageUrl)"
                  />
                </div>
              </div>
              <div v-truncate class="w-full">
                <div data-truncate-target>
                  {{ formatNumber(infringement.copyrights.length) }} copyrights
                </div>
              </div>
            </template>
            <template v-else>
              <div class="mr-2.5 size-[35px] shrink-0">
                <Thumbnail
                  :type="fileTypeToThumbnail(infringement.copyrights[0].type)"
                  :src="
                    previewImageUrl(
                      infringement.copyrights[0].asset?.previewImageUrl
                    )
                  "
                />
              </div>
              <div v-truncate class="w-full">
                <div data-truncate-target>
                  {{ infringement.copyrights[0].name }}
                </div>
              </div>
            </template>
          </Table.Cell>

          <div class="flex w-[50px] shrink-0 items-center">
            <SortIcon class="size-6 rotate-90 text-gray-800" />
          </div>

          <Table.Cell class="h-[52px]! flex-1 pl-[7px]">
            <div class="mr-2.5 size-[35px] shrink-0">
              <Thumbnail
                :type="fileTypeToThumbnail(infringement.infringingFile.type)"
                :src="
                  infringement.infringingFile.asset.previewImageUrl || undefined
                "
              />
            </div>
            <div v-truncate class="w-full">
              <div data-truncate-target>
                {{ infringement.infringingFile.name }}
              </div>
            </div>
          </Table.Cell>

          <Table.Cell class="h-[52px]! w-[180px]">
            <span v-if="isProcessing(infringement)">—</span>
            <template v-else>{{
              formatTimestamp(infringement.timestamp)
            }}</template>
          </Table.Cell>

          <Table.Cell class="h-[52px]! w-[180px]">
            <CreatingReport v-if="isProcessing(infringement)">
              <Tooltip
                size="lg"
                class="max-w-[301px]!"
                content="Generating a report can take a while. We’ll notify you once it's finished processing."
              >
                <span class="size-3.5" aria-hidden="true" />
              </Tooltip>
            </CreatingReport>
            <span v-else-if="isGroup(infringement)" class="text-gray-400"
              >—</span
            >
            <span v-else-if="coverOnly(infringement)" class="text-green-700"
              >Accepted cover match</span
            >
            <div
              v-else-if="
                isReport(infringement) && infringement.matches?.score != null
              "
              class="flex items-center gap-x-2"
            >
              <div
                class="w-10 shrink-0 text-[14px] font-medium"
                :class="accentText(infringement)"
              >
                {{ formatNumber(score(infringement)) }}%
              </div>
              <div class="w-[100px]">
                <Progress
                  :percentage="score(infringement)"
                  size="sm"
                  :color="levelColor(infringement)"
                  track-color="var(--color-gray-200)"
                />
              </div>
            </div>
            <span
              v-else
              class="inline-flex items-center rounded-[4px] px-[6px] py-[4px] text-[12px] leading-[12px]"
              :class="accentBg(infringement)"
            >
              {{ levelLabel(infringement) }}
            </span>
          </Table.Cell>

          <Table.Cell class="h-[52px]! w-[120px]">
            <Pill
              variant="default"
              :icon="labelIcon(infringement)"
              :class="{ 'text-gray-800!': isReportLabel(infringement) }"
            >
              {{ isReportLabel(infringement) ? 'Report' : 'Flagged' }}
            </Pill>
          </Table.Cell>

          <Table.Cell class="h-[52px]! w-[100px]">
            <RouterLink
              v-if="isGroup(infringement)"
              :to="rowLink(infringement)"
              @click.stop
            >
              <Button variant="stroke" size="md">
                View all {{ formatNumber(infringement.copyrights.length) }}
              </Button>
            </RouterLink>
            <Button
              v-else-if="!isReportLabel(infringement)"
              variant="stroke"
              size="md"
              @click.stop="handleGetReport(infringement)"
            >
              Get report
            </Button>
          </Table.Cell>
        </Table.Row>
      </div>
    </div>
  </Table>
</template>

<script setup lang="ts">
import { isAcceptedCoverMatch } from '@/features/infringement/utils/acceptedCover'
import { computed, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import {
  Table,
  Button,
  Pill,
  Progress,
  Thumbnail,
  Skeleton,
  Tooltip,
  type TableSort,
} from '@dacr/library'
import { SortIcon, FileIssueIcon, FlagIcon } from '@dacr/icons'
import {
  InfringementLabel,
  ReportStatus,
  ScoreLevel,
  type InfringementListQuery,
} from '@/shared/graphql/router/graphql'
import { formatTimestamp } from '@/shared/utils/datetime'
import { formatNumber } from '@/shared/utils/number'
import { fileTypeToThumbnail } from '@/shared/utils/thumbnail'
import { applyAuthorizationToken } from '@/shared/utils/url'
import { openDialog, Dialog } from '@/shared/dialogs/dialog'
import NoInfringements from './NoInfringements.vue'
import CreatingReport from './CreatingReport.vue'

type InfringementNode =
  InfringementListQuery['infringements']['edges'][number]['node']

type SortField = 'TIMESTAMP'
type SortDirection = 'ASC' | 'DESC'

export type InfringementSort = {
  field: SortField
  direction: SortDirection
}

const props = defineProps<{
  processing?: boolean
  loading?: boolean
  empty?: boolean
  infringements: InfringementNode[]
  sort: InfringementSort
}>()

const emit = defineEmits<{
  scroll: [event: Event]
  create: []
  sort: [sort: InfringementSort]
}>()

const scrollElement = ref<HTMLElement | null>(null)

defineExpose({ scrollElement })

const router = useRouter()

const sortModel = computed<TableSort | null>({
  get: (): TableSort => ({
    field: 'timestamp',
    direction: props.sort.direction === 'ASC' ? 'asc' : 'desc',
  }),
  set: (value: TableSort | null) => {
    if (!value) return
    emit('sort', {
      field: 'TIMESTAMP',
      direction: value.direction === 'asc' ? 'ASC' : 'DESC',
    })
  },
})

const isGroup = (i: InfringementNode) => i.copyrights.length > 1
const isReportLabel = (i: InfringementNode) =>
  i.label === InfringementLabel.Report
const isReport = (i: InfringementNode) => !isGroup(i) && isReportLabel(i)
const isProcessing = (i: InfringementNode) =>
  !isGroup(i) && i.report?.status === ReportStatus.Processing
const coverOnly = (i: InfringementNode) =>
  isAcceptedCoverMatch(i) && (i.matches?.score ?? 0) === 0
const score = (i: InfringementNode) => i.matches?.score ?? 0
const previewImageUrl = (url?: string | null) => applyAuthorizationToken(url)

const levelLabel = (i: InfringementNode) => {
  switch (i.matches?.level) {
    case ScoreLevel.Low:
      return 'Minimal match'
    case ScoreLevel.Medium:
      return 'Moderate match'
    case ScoreLevel.High:
      return 'Significant match'
    default:
      return 'Not assessed'
  }
}

const accentText = (i: InfringementNode) => ({
  'text-gray-300': i.matches?.level === ScoreLevel.Low,
  'text-yellow-500': i.matches?.level === ScoreLevel.Medium,
  'text-red-500': i.matches?.level === ScoreLevel.High,
})

const accentBg = (i: InfringementNode) => ({
  'bg-gray-100 text-gray-800': !i.matches,
  'text-white': !!i.matches,
  'bg-gray-300': i.matches?.level === ScoreLevel.Low,
  'bg-yellow-500': i.matches?.level === ScoreLevel.Medium,
  'bg-red-500': i.matches?.level === ScoreLevel.High,
})

const levelColor = (i: InfringementNode) => {
  switch (i.matches?.level) {
    case ScoreLevel.Medium:
      return 'var(--color-yellow-500)'
    case ScoreLevel.High:
      return 'var(--color-red-500)'
    default:
      return 'var(--color-gray-300)'
  }
}

const labelIcon = (i: InfringementNode) =>
  isReportLabel(i) ? FileIssueIcon : FlagIcon

const stackedStyle = (index: number, total: number) => {
  const count = Math.min(total, 3)
  const rotate = index === count - 1 ? 0 : index === 0 ? -10 : 10
  return { transform: `rotate(${rotate}deg)` }
}

const isFlagged = (i: InfringementNode) => !isGroup(i) && !isReportLabel(i)

const rowLink = (i: InfringementNode) =>
  isGroup(i)
    ? `/infringement/group/${i.infringingFile.id}/${new Date(i.timestamp).getTime()}`
    : `/infringement/report/${i.report?.id}`

const handleGetReport = (i: InfringementNode) =>
  openDialog(Dialog.FlaggedMatch, { props: { infringement: i } })

const onRowClick = (i: InfringementNode) => {
  if (isProcessing(i)) return
  if (isFlagged(i)) {
    handleGetReport(i)
    return
  }
  router.push(rowLink(i))
}
</script>
